import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  IconButton, 
  CircularProgress, 
  Paper
} from "@mui/material";
import { 
  CloudUpload, 
  Delete, 
  CheckCircle, 
  Error, 
  HourglassEmpty 
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import { useOnboarding } from "context/OnboardingContext";
import { prefillApi } from "../../services/onboarding/prefillApi";

const DocumentUpload = ({ sessionId }) => {
  const { sessionToken } = useOnboarding();
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Polling for document status
  useEffect(() => {
    let pollInterval;
    const pollStatus = async () => {
      if (isUploading) return;
      
      try {
        const docs = await prefillApi.getDocuments({ sessionId, sessionToken });
        setFiles(prev => {
          // Check if the data has actually changed to avoid unnecessary re-renders
          const currentStatusString = prev.map(f => f.status).join(',');
          const newStatusString = docs.map(doc => doc.status).join(',');
          
          if (currentStatusString === newStatusString && prev.length === docs.length) {
            return prev;
          }

          // Map API documents to our local file state
          const updatedFiles = docs.map(doc => ({
            name: doc.original_filename,
            status: doc.status,
            id: doc.document_id,
            error: doc.failure_message
          }));
          
          // Keep files that are still in 'uploading' state locally
          const uploadingLocally = prev.filter(f => f.status === 'uploading');
          return [...uploadingLocally, ...updatedFiles];
        });
      } catch (err) {
        console.error("Error polling document status", err);
      }
    };

    pollInterval = setInterval(pollStatus, 5000); // Increased interval to 5 seconds
    return () => clearInterval(pollInterval);
  }, [sessionId, sessionToken, isUploading]);

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    const newFiles = selectedFiles.map(file => ({
      name: file.name,
      status: 'uploading',
      progress: 0,
      id: null
    }));
    setFiles(prev => [...prev, ...newFiles]);

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        try {
          const result = await prefillApi.uploadDocument({ 
            sessionId, 
            sessionToken, 
            file 
          });
          setFiles(prev => prev.map((f, idx) => 
            (prev.length - selectedFiles.length + i === idx) 
              ? { ...f, status: 'processing', id: result.document_id } 
              : f
          ));
        } catch (err) {
          setFiles(prev => prev.map((f, idx) => 
            (prev.length - selectedFiles.length + i === idx) 
              ? { ...f, status: 'failed', error: err.message } 
              : f
          ));
        }
      }
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = async (id, index) => {
    if (id) {
      try {
        await prefillApi.deleteDocument({ sessionId, sessionToken, documentId: id });
      } catch (e) {
        console.error("Failed to delete document", e);
      }
    }
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <MDBox mb={4}>
      <Typography variant="h6" mb={2}>
        Upload Onboarding Documents
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        Upload PDFs, Images, or Spreadsheets to prefill your information.
      </Typography>

      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center"
        border="2px dashed"
        borderColor="primary.main"
        borderRadius="16px"
        p={4}
        sx={{ 
          cursor: 'pointer', 
          transition: 'all 0.3s',
          '&:hover': { bgcolor: 'primary.light', opacity: 0.8 }
        }}
        onClick={() => document.getElementById('file-input').click()}
      >
        <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="button" fontWeight="bold">
          Click or Drag & Drop Files
        </Typography>
        <input 
          id="file-input" 
          type="file" 
          multiple 
          hidden 
          onChange={handleFileChange} 
        />
      </Box>

      {files.length > 0 && (
        <Box mt={3}>
          {files.map((file, index) => (
            <Paper 
              key={index} 
              variant="outlined" 
              sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', borderRadius: '12px' }}
            >
              <Box flexGrow={1} display="flex" alignItems="center">
                {file.status === 'uploading' && <CircularProgress size={20} sx={{ mr: 2 }} />}
                {file.status === 'processing' && <HourglassEmpty sx={{ mr: 2, color: 'warning.main' }} />}
                {file.status === 'ready' && <CheckCircle sx={{ mr: 2, color: 'success.main' }} />}
                {file.status === 'failed' && <Error sx={{ mr: 2, color: 'error.main' }} />}
                
                <Typography variant="body2" noWrap sx={{ maxWidth: '200px' }}>
                  {file.name}
                </Typography>
                <Typography variant="caption" ml={2} color="textSecondary">
                  {file.status.toUpperCase()}
                </Typography>
              </Box>
              
              <IconButton size="small" onClick={() => removeFile(file.id, index)}>
                <Delete fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </Box>
      )}
    </MDBox>
  );
};

export default DocumentUpload;
